import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { normalizeIndianMobile } from '../utils/phone';
import { AlertTriangle, MessageCircle, Copy, Check } from 'lucide-react';

// Moved verbatim out of SeatingSheet.js (Stage 5) so the Table Links screen
// can reissue a link through the SAME modal and the same create-table-link
// call. Nothing about the create flow changed; `initial` only prefills the
// form (reissue = cancel the old link, then this modal with the old details
// and a fresh expiry).
const labelStyle = {
  display:'block', fontSize:'0.68rem', fontWeight:700,
  color:'var(--color-ink-muted)', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:5,
};
const inputStyle = {
  width:'100%', background:'#ffffff', border:'1.5px solid var(--color-border-strong)',
  borderRadius:8, padding:'9px 11px', color:'#0A1628', fontSize:'0.88rem',
  outline:'none', boxSizing:'border-box',
};

// ─── Modal: Send booking link ─────────────────────────────────────────────────

// Creates a private, time-limited booking link for the selected tables through
// the create-table-link edge function — ONE server-side operation (customer
// row, hold, link row, WhatsApp), so a half-made link cannot exist. The browser
// sends names and counts only: the price is computed server-side from the
// pricing table, the token is minted by the database, and the expiry travels
// as minutes. supabase.functions.invoke forwards this staff login's JWT, and
// the function places the hold AS that user (the hold RPC is staff-only).
const inr = n => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const LINK_EXPIRY_SETTING = 'table_link_expiry_minutes';
const LINK_EXPIRY_FALLBACK = 30;

export default function SendLinkModal({ selectedTables, displayDate, inputDate, cruise, deck, initial = null, onCreated, onClose }) {
  // `initial` (reissue): { name, phone, adults, children, infants, override, reason }.
  // Expiry is never prefilled — a reissued link always gets the default, fresh expiry.
  const [name, setName]           = useState(initial?.name ?? '');
  const [phone, setPhone]         = useState(initial?.phone ?? '');
  const [adults, setAdults]       = useState(initial?.adults ?? 2);
  const [children, setChildren]   = useState(initial?.children ?? 0);
  const [infants, setInfants]     = useState(initial?.infants ?? 0);
  const [override, setOverride]   = useState(initial?.override ?? '');   // '' = charge the standard price
  const [reason, setReason]       = useState(initial?.reason ?? '');
  const [expiry, setExpiry]       = useState('');
  const [expiryDefault, setExpiryDefault] = useState(LINK_EXPIRY_FALLBACK);
  const [rates, setRates]         = useState(null);        // { adult, child } from the pricing table
  const [ratesStatus, setRatesStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [result, setResult]       = useState(null);        // create-table-link response
  const [copied, setCopied]       = useState(false);
  // Frozen at mount. The parent's live selection can shrink underneath an open
  // modal (the seating sheet drops held tables from it as soon as the new hold
  // is fetched); the form and the result must keep describing the tables the
  // link was actually made for.
  const [tableList] = useState(() => [...selectedTables]);

  // Escape: cancel the form, or dismiss the result (the only ways out besides
  // the buttons). Never while a send is in flight — the result must get shown.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (submitting) return;
      onClose(!!result);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, result, onClose]);

  // Default expiry from app_settings (changing it never needs a deploy).
  useEffect(() => {
    let cancelled = false;
    supabase.from('app_settings').select('value').eq('key', LINK_EXPIRY_SETTING).maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const n = parseInt(data?.value, 10);
        const d = Number.isInteger(n) && n >= 1 && n <= 1440 ? n : LINK_EXPIRY_FALLBACK;
        setExpiryDefault(d);
        setExpiry(String(d));
      });
    return () => { cancelled = true; };
  }, []);

  // Live standard price for this cruise + deck — the same pricing-table read
  // the payment function and create-table-link make. Display only: the server
  // recomputes it and never trusts a figure from here.
  useEffect(() => {
    let cancelled = false;
    setRatesStatus('loading'); setRates(null);
    supabase.from('pricing').select('passenger_type, price')
      .eq('cruise_type', cruise).eq('deck', deck).in('passenger_type', ['Adult', 'Child'])
      .then(({ data, error: err }) => {
        if (cancelled) return;
        let adult = null, child = null;
        (data || []).forEach(r => {
          if (r.passenger_type === 'Adult') adult = Number(r.price);
          if (r.passenger_type === 'Child') child = Number(r.price);
        });
        if (!err && Number.isFinite(adult) && adult > 0 && Number.isFinite(child) && child >= 0) {
          setRates({ adult, child }); setRatesStatus('ok');
        } else {
          setRatesStatus('error');
        }
      });
    return () => { cancelled = true; };
  }, [cruise, deck]);

  const nA = Math.max(0, parseInt(adults, 10) || 0);
  const nC = Math.max(0, parseInt(children, 10) || 0);
  const nI = Math.max(0, parseInt(infants, 10) || 0);
  const standard = rates ? nA * rates.adult + nC * rates.child : null;

  const overrideNum = override === '' ? null : Number(override);
  const overrideBad = overrideNum !== null && (!Number.isFinite(overrideNum) || overrideNum <= 0);
  const overrideDiffers = overrideNum !== null && !overrideBad && standard !== null
    && Math.round(overrideNum) !== Math.round(standard);
  const reasonMissing = overrideDiffers && !reason.trim();
  const amountToPay = overrideDiffers ? Math.round(overrideNum) : standard;

  const phoneCheck = normalizeIndianMobile(phone);
  const phoneErr = phone.trim() && !phoneCheck.valid ? 'Enter a 10-digit mobile number.' : '';

  const expiryNum = parseInt(expiry, 10);
  const expiryBad = !(Number.isInteger(expiryNum) && expiryNum >= 1 && expiryNum <= 1440);

  const canSend = !submitting && !!name.trim() && phoneCheck.valid && nA >= 1
    && ratesStatus === 'ok' && standard > 0 && !overrideBad && !reasonMissing && !expiryBad;

  const handleSend = async () => {
    if (!canSend) return;
    setSubmitting(true); setError('');
    try {
      const resp = await supabase.functions.invoke('create-table-link', {
        body: {
          customerName: name.trim(),
          phone: phoneCheck.number,
          cruiseName: cruise,
          deck,
          date: inputDate,                    // YYYY-MM-DD
          tableNames: tableList,
          adults: nA, children: nC, infants: nI,
          overrideAmount: overrideDiffers ? Math.round(overrideNum) : undefined,
          overrideReason: overrideDiffers ? reason.trim() : undefined,
          expiryMinutes: expiryNum,
        },
      });
      if (resp.error) {
        // Non-2xx: 401/403 (not staff), 500. The body carries { error, message }.
        let msg = resp.error.message || 'Could not create the link.';
        try { const ctx = await resp.error.context?.json?.(); if (ctx?.message) msg = ctx.message; else if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
        setError(msg); setSubmitting(false); return;
      }
      const data = resp.data;
      // Expected refusals (tables taken, blocked, held, bad date…) come back 200
      // with { error, message, tables } so the message can be shown as-is.
      if (!data || data.error || !data.link) {
        setError(data?.message || 'Could not create the link.'); setSubmitting(false); return;
      }
      // Result stays up until staff press Done or Escape — no timer, no
      // auto-close on either outcome. It is the only place the link and a
      // WhatsApp failure reason are shown. onCreated only refreshes the parent.
      setResult(data);
      setSubmitting(false);
      onCreated?.(data);
    } catch (err) {
      setError(err.message || 'Could not create the link.'); setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(result.link); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard unavailable — the link is visible to select by hand */ }
  };

  // ── Result view ─────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="ss-modal-backdrop">
        <div className="ss-modal" style={{ maxWidth:480 }}>
          <div style={{ width:44, height:44, background: result.whatsappSent ? '#DCFCE7' : '#FEF3C7', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:14 }}>
            {result.whatsappSent
              ? <Check size={22} strokeWidth={2.5} color="#15803d" aria-hidden="true" />
              : <AlertTriangle size={22} strokeWidth={2} color="#B45309" aria-hidden="true" />}
          </div>
          <h3 style={{ fontSize:'1rem', fontWeight:800, color:NAVY, marginBottom:6 }}>
            {result.whatsappSent ? 'Link sent on WhatsApp' : 'Link created — WhatsApp did not go'}
          </h3>
          <p style={{ fontSize:'0.85rem', color:'#4a5a7a', marginBottom:12 }}>
            {result.whatsappSent
              ? <>Sent to <strong>+91 {phoneCheck.number}</strong> for <strong>{tableList.join(', ')}</strong>. The tables are held until <strong>{result.expiresLabel}</strong>.</>
              : <>{result.whatsappError || 'The message was not delivered.'} The tables are still held until <strong>{result.expiresLabel}</strong> — send this link to +91 {phoneCheck.number} by hand.</>}
          </p>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10 }}>
            <input type="text" readOnly value={result.link} onFocus={e=>e.target.select()}
              style={{ ...inputStyle, fontSize:'0.78rem', fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace', background:'var(--color-sunken)' }} />
            <button type="button" className="btn btn-outline btn-sm" onClick={copyLink} style={{ flexShrink:0, display:'inline-flex', alignItems:'center', gap:5 }}>
              {copied ? <Check size={14} strokeWidth={2} aria-hidden="true" /> : <Copy size={14} strokeWidth={1.75} aria-hidden="true" />}{copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:18 }}>
            {nA + nC + nI} guest{nA + nC + nI !== 1 ? 's' : ''} · {inr(result.amount)}{result.overrideReason ? ` (standard ${inr(result.standardAmount)} — ${result.overrideReason})` : ''}
          </p>
          <div style={{ display:'flex', gap:10 }}>
            <button className="btn btn-gold" onClick={()=>onClose(true)}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form view ───────────────────────────────────────────────────────────────
  return (
    <div className="ss-modal-backdrop" onClick={()=>onClose(false)}>
      <div className="ss-modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:520, maxHeight:'90vh', display:'flex', flexDirection:'column' }}>
        <h3 style={{ fontSize:'0.95rem', fontWeight:800, color:NAVY, marginBottom:2 }}>{initial ? 'Reissue booking link' : 'Send booking link'}</h3>
        <p style={{ fontSize:'0.78rem', color:'var(--color-ink-muted)', marginBottom:16 }}>
          {displayDate} · {cruise} · <strong>{deck}</strong> · Tables: <strong>{tableList.join(', ')}</strong>
        </p>
        <div style={{ overflowY:'auto', flex:1, paddingRight:4 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Customer name <span style={{ color:'#DC2626' }}>*</span></label>
              <input type="text" value={name} onChange={e=>setName(e.target.value)} placeholder="Name on the booking" style={inputStyle} autoFocus />
            </div>
            <div>
              <label style={labelStyle}>WhatsApp number <span style={{ color:'#DC2626' }}>*</span></label>
              <input type="tel" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="10 digits" style={{ ...inputStyle, ...(phoneErr ? { borderColor:'#DC2626' } : {}) }} />
              {phoneErr && <span style={{ fontSize:'0.68rem', color:'#dc2626', marginTop:3, display:'block' }}>{phoneErr}</span>}
            </div>
            <div>
              <label style={labelStyle}>Adults <span style={{ color:'#DC2626' }}>*</span></label>
              <input type="number" min="1" value={adults} onChange={e=>setAdults(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={labelStyle}>Children</label>
                <input type="number" min="0" value={children} onChange={e=>setChildren(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Infants</label>
                <input type="number" min="0" value={infants} onChange={e=>setInfants(e.target.value)} style={inputStyle} />
              </div>
            </div>

            {/* Standard price — live from the pricing table, clearly labelled */}
            <div style={{ gridColumn:'1/-1', background:'var(--color-sunken)', border:'1px solid var(--color-border)', borderRadius:8, padding:'10px 12px' }}>
              <div style={{ fontSize:'0.68rem', fontWeight:700, color:'var(--color-ink-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:3 }}>Standard price (pricing table)</div>
              {ratesStatus === 'ok' && (
                <div style={{ fontSize:'1rem', fontWeight:800, color:NAVY }}>
                  {inr(standard)}
                  <span style={{ fontSize:'0.74rem', fontWeight:600, color:'var(--color-ink-muted)', marginLeft:8 }}>
                    {nA}×{inr(rates.adult)}{nC > 0 ? ` + ${nC}×${inr(rates.child)}` : ''}{nI > 0 ? ` · ${nI} infant${nI !== 1 ? 's' : ''} free` : ''}
                  </span>
                </div>
              )}
              {ratesStatus === 'loading' && <div style={{ fontSize:'0.82rem', color:'var(--color-ink-muted)' }}>Loading price…</div>}
              {ratesStatus === 'error' && <div style={{ fontSize:'0.82rem', fontWeight:600, color:'#dc2626' }}>Price couldn't be loaded for {cruise} · {deck}. A link can't be sent until it loads.</div>}
            </div>

            <div>
              <label style={labelStyle}>Charge a different amount (₹)</label>
              <input type="number" min="1" value={override} onChange={e=>setOverride(e.target.value)} placeholder="Leave empty for standard"
                style={{ ...inputStyle, ...(overrideBad ? { borderColor:'#DC2626' } : {}) }} />
              {overrideBad && <span style={{ fontSize:'0.68rem', color:'#dc2626', marginTop:3, display:'block' }}>Must be a positive amount.</span>}
            </div>
            <div>
              <label style={labelStyle}>Reason{overrideDiffers && <span style={{ color:'#DC2626' }}> *</span>}</label>
              <input type="text" value={reason} onChange={e=>setReason(e.target.value)} disabled={!overrideDiffers}
                placeholder={overrideDiffers ? 'Why the price differs' : 'Only when the amount differs'}
                style={{ ...inputStyle, ...(reasonMissing ? { borderColor:'#DC2626' } : {}), ...(!overrideDiffers ? { background:'var(--color-sunken)' } : {}) }} />
              {reasonMissing && <span style={{ fontSize:'0.68rem', color:'#dc2626', marginTop:3, display:'block' }}>A reason is required when the amount differs from standard.</span>}
            </div>

            <div>
              <label style={labelStyle}>Link expires in (minutes)</label>
              <input type="number" min="1" max="1440" value={expiry} onChange={e=>setExpiry(e.target.value)}
                style={{ ...inputStyle, ...(expiryBad ? { borderColor:'#DC2626' } : {}) }} />
              <span style={{ fontSize:'0.68rem', color: expiryBad ? '#dc2626' : 'var(--color-ink-muted)', marginTop:3, display:'block' }}>
                {expiryBad ? '1 to 1440 minutes.' : `Default ${expiryDefault} min · tables stay held for this long`}
              </span>
            </div>
            <div>
              <label style={labelStyle}>Customer pays</label>
              <input type="text" readOnly value={standard !== null && !overrideBad ? inr(amountToPay) : ''} style={{ ...inputStyle, color:'var(--color-accent-strong)', fontWeight:700, background:'var(--color-sunken)' }} />
            </div>
          </div>
        </div>
        {error && (
          <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:8, padding:'8px 12px', fontSize:'0.78rem', color:'#dc2626', marginTop:12, fontWeight:600 }}>
            {error}
          </div>
        )}
        <div style={{ display:'flex', gap:10, marginTop:16, flexShrink:0 }}>
          <button className="btn btn-gold" onClick={handleSend} disabled={!canSend} style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
            <MessageCircle size={15} strokeWidth={1.75} aria-hidden="true" />{submitting ? 'Sending…' : 'Send link via WhatsApp'}
          </button>
          <button className="btn btn-outline" onClick={()=>onClose(false)} disabled={submitting}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
